import { Box, Button, Flex, Skeleton, Text } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import {
  InstallationContext,
  PerformanceStepTabKey,
} from 'components/machine/dialogs/installation/context';
import { CommonStepHeader } from 'components/machine/dialogs/installation/header';
import { BallTypeResults } from 'components/machine/dialogs/installation/steps/ball-type/results';
import { DataCollectorHoC } from 'components/machine/dialogs/installation/steps/data-collector';
import { MachineContext } from 'contexts/machine.context';
import { SubStatus } from 'enums/installation';
import { t } from 'i18next';
import { InstallStep } from 'lib_ts/enums/installation';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ICollectionShotGroup } from 'lib_ts/interfaces/training/i-machine-shot';
import { useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { PitchListsService } from 'services/pitch-lists.service';
import useMiscStore from 'stores/use-misc-store';

interface IProps {
  state: SubStatus;
  onStart: () => void;
  onComplete: () => Promise<void>;
  updateFiringLog?: (agg: ICollectionShotGroup[]) => void;
}

export const StepBallTypeCalibration = (props: IProps) => {
  const { installation, btc, btcTab, setBtcTab } =
    useContext(InstallationContext);
  const { machine } = useContext(MachineContext);
  const [listName, setListName] = useState<string>();

  const [loading, setLoading] = useState(false);

  // after the first time, it will not force you to go back
  const [goToResults, setGoToResults] = useState(true);

  // list name for subheading
  useEffect(() => {
    if (!btc?.listID) {
      return;
    }

    const callback = async () => {
      try {
        setLoading(true);

        const list = await PitchListsService.getInstance().getOne(btc.listID);
        if (!list) {
          throw new Error(
            `Failed to find list with ID ${btc.listID} for data collection`
          );
        }

        setListName(list?.name);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    callback();
  }, [btc?.listID]);

  useEffect(() => {
    if (goToResults && installation.model_id) {
      setGoToResults(false);
      setBtcTab(PerformanceStepTabKey.Results);
    }
  }, [installation.model_id, goToResults]);

  return (
    <ErrorBoundary identifier="StepBallTypeCalibration">
      <Flex
        direction="column"
        gap="4"
        style={{
          height: '100%',
        }}
      >
        {installation.standalone !== InstallStep.BallType && (
          <Flex direction="column" gap="1">
            <CommonStepHeader label="common.ball-type-calibration" />
            {listName && (
              <Text color={RADIX.COLOR.HINT} size={RADIX.TEXT.SIZE.HINT}>
                {t('common.pitch-list')}: {listName}
              </Text>
            )}
          </Flex>
        )}

        {props.state === SubStatus.NotStarted && (
          <Flex
            flexGrow="1"
            direction="column"
            gap="5"
            align="center"
            justify="center"
          >
            <Box>
              <img
                style={{
                  width: '176px',
                }}
                src={useMiscStore
                  .getState()
                  .getThemeImgPath('/renders/ball-type-calibration.svg')}
              />
            </Box>

            <Text align="center" color={RADIX.COLOR.SECONDARY}>
              {t('admin.btc-description')}{' '}
              <Trans
                i18nKey="admin.only-x-ball-type"
                t={t}
                values={{
                  x: machine.ball_type,
                }}
                components={[<strong key="0" />]}
              />
            </Text>

            <Box>
              <Button onClick={props.onStart}>
                Start Ball Type Calibration
              </Button>
            </Box>
          </Flex>
        )}

        {props.state !== SubStatus.NotStarted && (
          <Skeleton loading={loading}>
            <Box flexGrow="1">
              <CommonTabs
                tabs={[
                  {
                    label: 'Data Collection',
                    value: PerformanceStepTabKey.DataCollection,
                    content: btc ? (
                      <DataCollectorHoC
                        identifier="BtcDataCollector"
                        // for drawing error instead of complete
                        state={props.state}
                        id={btc._id}
                        onComplete={props.onComplete}
                        updateFiringLog={props.updateFiringLog}
                      />
                    ) : (
                      <CommonCallout text="Data collection cannot proceed without a valid collection ID." />
                    ),
                  },
                  {
                    label: 'Results',
                    value: PerformanceStepTabKey.Results,
                    content: <BallTypeResults machineID={machine.machineID} />,
                    invisible: !installation.model_id,
                  },
                ]}
                value={btcTab}
                onValueChange={(value) =>
                  setBtcTab(value as PerformanceStepTabKey)
                }
              />
            </Box>
          </Skeleton>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
